import { Dispatch, FC, SetStateAction } from 'react'
import { Box, Dialog, H3, H5, IconButton, makeStyles } from '@perk-ui/core'
import clsx from 'clsx'
import { parseISO } from 'date-fns'
import { X } from 'react-feather'
import { useParams } from 'react-router'

import { useSitesContext } from '../../../components/SitesProvider'
import {
  surveyPeriodsOrdered,
  UROLOGY_CONDITION,
} from '../../../config/constants'
import usePatient from '../../../features/query-hooks/usePatient'
import useSurveyCompletionCDStatus, {
  SurveyStatusObject,
  SurveyStatusPeriod,
} from '../../../features/query-hooks/useSurveyCompletionCDStatus'
import {
  SurveyResponse,
  SurveyResponseAnswer,
} from '../../../features/query-hooks/useSurveyResponse'
import useSurveyResponseGroupsAlt from '../../../features/query-hooks/useSurveyResponseGroupsAlt'
import useDates from '../../../hooks/useDates'
import SurveyCompletion from './SurveyCompletion'
import {
  getSurveyScore,
  getSurveyStatus,
  statusProperties,
} from './SurveyTable'

interface SurveyAnswersModalProps {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  setModalAnswers: Dispatch<React.SetStateAction<SurveyResponse | null>>
  survey: SurveyResponse | null
  timezone: string
  isGroupView: boolean
}

interface SliderAnswerOptions {
  maxValue?: string
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    '& .MuiDialog-container': {
      '& .MuiPaper-root:not(.MuiAlert-root)': {
        minWidth: '500px',
      },
    },
  },
  modalHeader: {
    padding: theme.spacing(1, 2, 0, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& h3': {
      fontSize: '16px',
      fontWeight: 400,
      textTransform: 'capitalize',
    },
  },
  modalContent: {
    padding: theme.spacing(1, 2),
  },
  modalCloseButton: {},
  menuContainer: {
    height: '100%',
  },
  menuContent: {
    borderRight: '1px solid #F1F1F1',
    height: '100%',
    padding: theme.spacing(2),
  },
  gridContainer: {
    height: '100%',
  },
  contentContainer: {
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    padding: theme.spacing(2),
    height: '100%',
    overflow: 'hidden',
  },
  contentTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    '& h3': {
      margin: 0,
      fontSize: 20,
      fontWeight: 700,
    },
    '& span': {
      fontSize: 24,
      fontWeight: 700,
      paddingRight: theme.spacing(3),
    },
  },
  linkContainer: {
    background: theme.palette.background.paper,
    // Somewhat arbitrary sizing, but matches design
    width: '100%',
    height: '100%',
    maxWidth: 290,
    padding: theme.spacing(0, 0, 2, 0),
  },
  questionContainer: {
    borderRadius: 8,
    border: '1px solid #F1F1F1',
    background: '#FAFAFB',
    padding: theme.spacing(1.5),
    margin: theme.spacing(1, 0),
  },
  dateCompleted: {
    fontSize: 14,
    fontWeight: 500,
    color: '#A3A3A3',
    margin: theme.spacing(2, 0),
  },
  dateOpen: {
    fontSize: 14,
    fontWeight: 700,
    color: '#F79A48',
    margin: theme.spacing(5, 0),
  },
  marginRight: {
    marginRight: theme.spacing(1),
  },
  questionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#000000',
  },
  link: {
    margin: theme.spacing(0.5, 0),
    padding: theme.spacing(1.5, 2),
    borderRadius: 16,
    '&:hover': {
      background: 'rgba(87, 153, 173, 0.04)',
    },
    '& .ListItemText': {
      margin: 0,
      marginLeft: theme.spacing(1),
    },
    '& .MuiListItemIcon-root': {
      height: 30,
    },
    '& h5': {
      fontSize: 16,
      fontWeight: 700,
    },
    '& svg': {
      margin: 'auto',
    },
    '& span': {
      margin: 'auto',
    },
  },
  active: {
    // css specificity hack to override `a:link`
    '&&': {
      color: theme.palette.primary.main,
    },
    background: '#EAF7FD',
    '& svg': {
      color: theme.palette.primary.main,
    },
  },
  menuLinkIcon: {
    margin: 'auto',
    minWidth: 'unset',
    '& svg': {
      margin: 'auto',
    },
  },
  answerOption: {
    background: '#F3F3F3',
    color: '#A3A3A3',
    fontSize: 12,
    fontWeight: 400,
    textAlign: 'center',
    borderRadius: 28,
    padding: theme.spacing(0.5, 2),
    marginBottom: theme.spacing(1),
  },
  selectedAnswer: {
    background: '#DBF3FF',
    color: '#56AAE8',
    fontWeight: 700,
  },
  sliderAnswer: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
  },
  defaultAnswer: {
    color: '#6B6B6B',
    fontSize: 12,
  },
  scoreList: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
  },
  groupDetailsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    borderRadius: '4px',
    padding: '5px',
    margin: '5px 0',
  },
  bodyPartTitle: {
    margin: 0,
    maxWidth: '135px',
    marginRight: '5px',
    whiteSpace: 'break-spaces',
  },
  bodyPartListContainer: {
    overflow: 'auto',
    maxHeight: '100px',
  },
  longScoreLabel: {
    fontSize: '16px !important',
    display: 'inline-flex',
    alignItems: 'center',
  },
}))

const SurveyAnswersModal: FC<SurveyAnswersModalProps> = ({
  open,
  setOpen,
  survey,
  setModalAnswers,
  timezone,
  isGroupView,
}) => {
  const classes = useStyles()
  const { currentSite } = useSitesContext()
  const { localizedFormatDate } = useDates()
  const { caseId, patientId } = useParams<{
    patientId: string
    caseId: string
  }>()
  const { data: openSurveys = [] } = useSurveyResponseGroupsAlt({
    patientId: patientId,
    patientCaseId: caseId,
  })
  const { data: surveyCompletionList } = useSurveyCompletionCDStatus(
    patientId,
    caseId,
  )
  const { data: patient } = usePatient(patientId)
  // const [content, setContent] = useState<string>('profile')
  // const isUserAdmin = user?.staff?.isAdmin || false

  const handleDialogClose = () => {
    setOpen(false)
    setModalAnswers(null)
  }

  const showQuestionsAnswers = (answer: SurveyResponseAnswer) => {
    const questionType = answer.surveyQuestion.variant
    const sliderOptions = answer.surveyQuestion.data as SliderAnswerOptions
    const maxValue = parseInt(sliderOptions.maxValue || '0')
    // const maxValue = parseInt(answer.surveyQuestion.data?.maxValue || '0')
    switch (questionType) {
      case 'choice-long':
      case 'choice-dropdown':
      case 'choice-standard':
        return (
          <Box>
            {answer.surveyQuestion.data.answerOptions.map((choice, index) => {
              return (
                <Box
                  key={index}
                  className={clsx(classes.answerOption, {
                    [classes.selectedAnswer]: answer.values[0] === choice.value,
                  })}
                >
                  {choice.label}
                </Box>
              )
            })}
          </Box>
        )
      case 'slider':
        if (maxValue == 10) {
          return (
            <Box className={classes.sliderAnswer}>
              {[...Array(maxValue + 1)].map((_, index) => {
                return (
                  <Box
                    key={index}
                    className={clsx(classes.answerOption, {
                      [classes.selectedAnswer]:
                        answer.values[0] === index.toString(),
                    })}
                  >
                    {index}
                  </Box>
                )
              })}
            </Box>
          )
        } else {
          return (
            <Box className={classes.sliderAnswer}>
              {answer.values[0]}/{maxValue}
            </Box>
          )
        }

      default:
        return <Box className={classes.defaultAnswer}>{answer.values[0]}</Box>
        break
    }
  }
  const currentPeriodObj = surveyPeriodsOrdered.find(
    (period) =>
      period.slug ==
      survey?.metadata
        .find((meta) => meta.type === 'schedule')
        ?.value.toString()
        .toLowerCase(),
  )
  const periodType = currentPeriodObj?.type as SurveyStatusPeriod
  const statusDetails = (survey: SurveyStatusObject | undefined) => {
    if (!survey) {
      return null
    }
    const { groupItems } = survey

    return (
      <>
        <div className={classes.bodyPartListContainer}>
          <H5>Body Parts</H5>
          {groupItems.map((item) => {
            return (
              <Box
                key={item.patientCaseId}
                className={classes.groupDetailsContainer}
              >
                <p className={classes.bodyPartTitle}>
                  {/* {humanizeBodySite(item.bodySiteSlug)} */}
                  {item.bodySite}
                </p>
              </Box>
            )
          })}
        </div>
      </>
    )
  }
  const showContent = () => {
    const periodStatus = surveyCompletionList?.surveys?.[periodType]?.status
    const surveyStatus = survey
      ? getSurveyStatus(
          survey.surveyResponseScores,
          parseISO(survey.availableOn),
          parseISO(survey.expiresOn),
          timezone,
          survey,
        )
      : 'N/A'
    let viewStatus = 'not-open' as
      | 'open'
      | 'completed'
      | 'not-open'
      | 'expired'
      | 'N/A'
    switch (periodStatus) {
      case 'done':
        viewStatus = 'completed'
        break
      case 'expiring_soon':
        viewStatus = 'open'
        break
      case 'missed':
        viewStatus = 'expired'
        break
      case 'open':
        viewStatus = 'open'
        break
      case 'n/a':
        viewStatus = 'not-open'
        break
      default:
        viewStatus = 'N/A'
        break
    }
    if (!isGroupView) viewStatus = surveyStatus
    const dateText =
      viewStatus === 'completed' ? 'Completed on' : 'Complete before'

    return (
      <Box>
        <Box className={classes.contentTitle}>
          {viewStatus !== 'open' && <h3>{survey?.survey.name}</h3>}
          <span
            className={clsx({
              [classes.longScoreLabel]: survey?.survey.slug == 'IIEF-5',
            })}
          >
            {survey?.survey.slug !== 'UROLOGY' &&
              survey?.survey.slug !== 'TAMPON-TEST' &&
              survey?.survey.slug !== 'PROMIS-10' &&
              viewStatus !== 'open' &&
              getSurveyScore(
                surveyStatus,
                survey as SurveyResponse,
                statusProperties[viewStatus].label,
                true,
              )}

            {survey?.survey.slug == 'PROMIS-10' &&
              viewStatus !== 'open' &&
              survey?.surveyResponseScores.map((score) => {
                const value = score.value
                return (
                  <p key={score.name} className={classes.scoreList}>
                    {score.name}: {value}
                  </p>
                )
              })}

            {survey?.survey.slug == 'UROLOGY' &&
              viewStatus !== 'open' &&
              survey?.surveyResponseScores.map((score) => {
                let value = score.value
                if (score.name == 'Condition') {
                  value =
                    UROLOGY_CONDITION.find(
                      (condition) => condition.value === score.value,
                    )?.slug || ''
                }
                if (score.name == 'Back Pain') {
                  value = score.value === '1.0' ? 'Yes' : 'No'
                }
                return (
                  <p key={score.name} className={classes.scoreList}>
                    {score.name}: {value}
                  </p>
                )
              })}
          </span>
        </Box>
        {viewStatus === 'open' &&
          currentPeriodObj &&
          statusDetails(surveyCompletionList?.surveys[periodType])}
        <Box
          className={clsx({
            [classes.dateCompleted]: viewStatus === 'completed',
            [classes.dateOpen]: viewStatus === 'open',
          })}
        >
          {viewStatus === 'completed'
            ? `${dateText} ${localizedFormatDate(
                parseISO(survey?.completedAt || ''),
              )}`
            : viewStatus === 'open'
              ? `${dateText} ${localizedFormatDate(
                  parseISO(survey?.expiresOn || ''),
                )}`
              : ''}
        </Box>
        {viewStatus !== 'open' &&
          survey?.surveyResponseAnswers.map((answer) => {
            const questionSlug = answer.surveyQuestion.data.prompt
            return (
              <Box key={answer.id} className={classes.questionContainer}>
                <p className={classes.questionTitle}>
                  {questionSlug != null
                    ? questionSlug
                    : answer.surveyQuestion.slug}
                </p>
                {showQuestionsAnswers(answer)}
              </Box>
            )
          })}
        {viewStatus === 'open' && (
          <SurveyCompletion openSurveys={openSurveys} patient={patient} />
        )}
      </Box>
    )
  }

  const period = (survey?.metadata.find((meta) => meta.type === 'schedule')
    ?.value || '') as string
  let periodName = period
  if (period.includes('followup_')) {
    periodName = 'Session ' + parseInt(period.replace('followup_', '')) * 5
    if (currentSite?.organization.name === 'StemWave') {
      periodName = 'Session ' + parseInt(period.replace('followup_', ''))
    }
  }
  return (
    <Dialog onClose={handleDialogClose} open={open} className={classes.root}>
      <Box className={classes.gridContainer}>
        <Box className={classes.modalHeader}>
          <H3>{periodName}</H3>
          <IconButton
            id="user-settings-modal-close-button"
            className={classes.modalCloseButton}
            aria-label="close"
            onClick={handleDialogClose}
          >
            <X size={18} />
          </IconButton>
        </Box>
        <Box className={classes.modalContent}>{showContent()}</Box>
      </Box>
    </Dialog>
  )
}

export default SurveyAnswersModal
